import React from 'react';
import { Box, keyframes } from '@mui/material';
import { getStaticFileUrl } from '../../utils/staticUtils';
import AuthPlatformTagline from './AuthPlatformTagline';
import { LOGIN_ACCENT, mobileLoginBackground, mobileLoginOrb, AUTH_LOGO_STATIC_PATH } from './loginDarkTheme';
import useMobileKeyboardLayout from '../../hooks/useMobileKeyboardLayout';
import { getNativePlatform } from '../../utils/mobileView';

const floatUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

/** Full-screen dark shell shared by login, forgot password, reset password, OTP. */
export default function MobileAuthShell({ children, footer }) {
  const { keyboardHeight } = useMobileKeyboardLayout(true);
  const reduceEffects = getNativePlatform() === 'android';

  return (
    <Box
      className="login-page login-page--mobile-dark"
      sx={{
        ...mobileLoginBackground,
        position: 'fixed',
        inset: 0,
        minHeight: 0,
        height: 'auto',
        maxHeight: 'none',
        pt: 0,
        pb: 0,
      }}
    >
      <Box
        sx={mobileLoginOrb(
          {
            top: '-12%',
            right: '-20%',
            width: 280,
            height: 280,
            background: `radial-gradient(circle, ${LOGIN_ACCENT}33 0%, transparent 70%)`,
          },
          { reduceEffects },
        )}
      />
      <Box
        sx={mobileLoginOrb(
          {
            bottom: '-8%',
            left: '-18%',
            width: 240,
            height: 240,
            background: 'radial-gradient(circle, rgba(45, 212, 191, 0.18) 0%, transparent 70%)',
          },
          { reduceEffects },
        )}
      />
      {!reduceEffects ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.04,
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            pointerEvents: 'none',
          }}
        />
      ) : null}

      <Box
        className="mobile-auth-scroll-container"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          position: 'relative',
          zIndex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
        }}
      >
        <Box
          sx={{
            flex: '0 0 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            px: { xs: 2, sm: 3 },
            pt: { xs: 'calc(env(safe-area-inset-top, 0px) + 36px)', sm: 'calc(env(safe-area-inset-top, 0px) + 56px)' },
            pb: { xs: 3, sm: 4 },
            animation: reduceEffects ? 'none' : `${floatUp} 0.55s ease-out`,
          }}
        >
          <Box className="mobile-auth-logo-stage" sx={{ width: '100%', maxWidth: 420, textAlign: 'center', mb: 2.5 }}>
            <Box
              component="img"
              src={getStaticFileUrl(AUTH_LOGO_STATIC_PATH)}
              alt="RoomNexa"
              sx={{
                width: 80,
                height: 80,
                objectFit: 'contain',
                mx: 'auto',
                display: 'block',
              }}
            />
            <AuthPlatformTagline />
          </Box>

          <Box className="mobile-auth-card-stage" sx={{ width: '100%', maxWidth: 420 }}>{children}</Box>
        </Box>

        {footer ? (
          <Box sx={{ px: 2, pb: 2, pt: 1, textAlign: 'center' }}>{footer}</Box>
        ) : null}
        <Box
          aria-hidden="true"
          sx={{
            flex: '0 0 auto',
            height: keyboardHeight > 0 ? `${keyboardHeight + 24}px` : 0,
            transition: 'height 120ms ease-out',
          }}
        />
      </Box>
    </Box>
  );
}
